<template>
  <Modal
    title="Configuration details"
    :model-value="modelValue"
    v-on:update:model-value="(value: boolean) => $emit('update:modelValue', value)"
  >
    <template v-if="configurationId && workerConfiguration">
      <h1
        class="title"
        :title="workerConfiguration.name"
      >
        {{ truncateLong(workerConfiguration.name) }}
      </h1>
      <!-- Do not put any spaces or line breaks between the <pre> and its contents, or the JSON indentation will be messed up -->
      <pre>{{ prettify(workerConfiguration.configuration) }}</pre>
    </template>
  </Modal>
</template>

<script lang="ts">
import { mapStores } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import Modal from '@/components/Modal.vue'
import { truncateMixin } from '@/mixins'
import { useWorkerConfigurationStore } from '@/stores'
import type { UUID } from '@/types'
import type { WorkerVersion } from '@/types/worker'

export default defineComponent({
  components: {
    Modal,
  },
  mixins: [truncateMixin],
  emits: {
    'update:modelValue': (value: boolean) => typeof value === 'boolean',
  },
  props: {
    /*
     * This component supports two exclusives modes:
     * * Emitting a select-configuration event when the workerVersion property is set
     * * Managing worker runs property when workerRun property are set
     */
    modelValue: {
      type: Boolean,
      default: false,
    },
    workerVersion: {
      type: Object as PropType<WorkerVersion>,
      required: true,
    },
    configurationId: {
      type: String as PropType<UUID>,
      required: true,
    },
  },
  computed: {
    ...mapStores(useWorkerConfigurationStore),
    workerConfiguration() {
      if (!this.configurationId) return
      return this.workerConfigurationStore.configurations[this.workerVersion.worker.id]?.[
        this.configurationId
      ]
    },
  },
  methods: {
    prettify: function (value: unknown) {
      return JSON.stringify(value, null, 2)
    },
  },
  watch: {
    configurationId: {
      immediate: true,
      handler(newValue: UUID | null) {
        if (
          newValue !== null &&
          !this.workerConfigurationStore.configurations[this.workerVersion.worker.id]?.[newValue]
        )
          this.workerConfigurationStore.get(this.workerVersion.worker.id, newValue)
      },
    },
  },
})
</script>

<style scoped>
pre {
  /* Break long strings into multiple lines in the configuration JSON display */
  white-space: pre-wrap;
}
</style>
